"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import type { CSSProperties, ReactNode } from "react";
import { Suspense, useCallback, useMemo, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import BottomNavigation from "../navigation/BottomNavigation";
import { getActiveViewFromUrl } from "../navigation/ViewContext";
import BrainMobileWaves from "../brain/mobile/BrainMobileWaves";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import HeaderPlaceholder from "../header/HeaderPlaceholder";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { useDeepLinkNavigation } from "@/hooks/useDeepLinkNavigation";
import BrainMobileMessages from "../brain/mobile/BrainMobileMessages";
import { useSelector } from "react-redux";
import { selectEditingDropId } from "@/store/editSlice";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useAndroidKeyboard } from "@/hooks/useAndroidKeyboard";
import useCapacitor from "@/hooks/useCapacitor";
import PullToRefresh from "../providers/PullToRefresh";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import MemesQuickVoteDialog from "../brain/left-sidebar/waves/memes-quick-vote/MemesQuickVoteDialog";
const TouchDeviceHeader = dynamic(() => import("../header/AppHeader"), {
  ssr: false,
  loading: () => <HeaderPlaceholder />
});
interface Props {
  readonly children: ReactNode;
}
const STREAM_ROUTE_LOADING_BOTTOM_RESERVE = "--stream-route-loading-bottom-reserve";
const STREAM_ROUTE_LOADING_HEADER_RESERVE = "--stream-route-loading-header-reserve";
// Matches HeaderPlaceholder's default shell before LayoutContext measures it.
const STREAM_ROUTE_LOADING_HEADER_FALLBACK_RESERVE = "100px";
type StreamRouteLoadingReserveStyle = CSSProperties & {
  readonly [STREAM_ROUTE_LOADING_BOTTOM_RESERVE]: "0px" | "85px";
  readonly [STREAM_ROUTE_LOADING_HEADER_RESERVE]: string;
};
const streamRouteLoadingReserveVisibleStyle: StreamRouteLoadingReserveStyle = {
  [STREAM_ROUTE_LOADING_BOTTOM_RESERVE]: "85px",
  [STREAM_ROUTE_LOADING_HEADER_RESERVE]: STREAM_ROUTE_LOADING_HEADER_FALLBACK_RESERVE
};
function WavesQuickVoteView() {
  const $ = _c(8);
  const quickVote = useMemesQuickVoteDialogController();
  let t0;
  if ($[0] !== quickVote.openQuickVote || $[1] !== quickVote.prefetchQuickVote) {
    t0 = <BrainMobileWaves onOpenQuickVote={quickVote.openQuickVote} onPrefetchQuickVote={quickVote.prefetchQuickVote} />;
    $[0] = quickVote.openQuickVote;
    $[1] = quickVote.prefetchQuickVote;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== quickVote.dialogState) {
    t1 = <MemesQuickVoteDialog {...quickVote.dialogState} />;
    $[3] = quickVote.dialogState;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function AppLayoutFallback(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HeaderPlaceholder />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <main>{children}</main>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-h-[85px] tw-w-full" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <div className="tw-overflow-auto" style={streamRouteLoadingReserveVisibleStyle}>{t1}{t2}{t3}</div>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function AppLayoutContent(t0) {
  const $ = _c(40);
  const {
    children
  } = t0;
  useDeepLinkNavigation();
  const {
    registerRef,
    spaces
  } = useLayout();
  const {
    setHeaderRef
  } = useHeaderContext();
  const headerRef = useRef(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("drop");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isSingleDropOpen = t1 !== null;
  let t2;
  let waveParam;
  if ($[2] !== pathname || $[3] !== searchParams) {
    waveParam = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    });
    t2 = getActiveViewFromUrl({
      activeWaveId: waveParam,
      searchParams
    });
    $[2] = pathname;
    $[3] = searchParams;
    $[4] = t2;
    $[5] = waveParam;
  } else {
    t2 = $[4];
    waveParam = $[5];
  }
  const activeView = t2;
  let t3;
  if ($[6] !== searchParams) {
    t3 = searchParams.get("view");
    $[6] = searchParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const viewParam = t3;
  const hasWaveParam = Boolean(waveParam);
  const isViewingWavesOrMessages = viewParam === "waves" || viewParam === "messages";
  let t4;
  if ($[8] !== pathname) {
    t4 = pathname === "/waves" || pathname.startsWith("/waves/");
    $[8] = pathname;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const isWavesRoute = t4;
  let t5;
  if ($[10] !== pathname) {
    t5 = pathname === "/messages" || pathname.startsWith("/messages/");
    $[10] = pathname;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const isMessagesRoute = t5;
  const isStreamRoute = isWavesRoute || isMessagesRoute || pathname === "/notifications" || pathname === "/" && (hasWaveParam || isViewingWavesOrMessages);
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  const {
    isVisible: isAndroidKeyboardVisible,
    isAndroid
  } = useAndroidKeyboard();
  const {
    isIos,
    keyboardVisible: isIosKeyboardVisible
  } = useCapacitor();
  const isEditingOnMobile = isApp && editingDropId !== null;
  const isKeyboardVisible = isAndroid && isAndroidKeyboardVisible || isIos && isIosKeyboardVisible;
  const shouldHideBottomNav = isKeyboardVisible;
  let t6;
  if ($[12] !== registerRef || $[13] !== setHeaderRef) {
    t6 = node => {
      headerRef.current = node;
      registerRef("header", node);
      setHeaderRef(node);
    };
    $[12] = registerRef;
    $[13] = setHeaderRef;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const headerWrapperRef = t6;
  const isNavVisible = !isSingleDropOpen && !isEditingOnMobile && !shouldHideBottomNav;
  const routeLoadingHeaderReserve = spaces.measurementsComplete ? `${spaces.headerSpace}px` : STREAM_ROUTE_LOADING_HEADER_FALLBACK_RESERVE;
  const t7 = isNavVisible ? "85px" : "0px";
  let t8;
  if ($[15] !== routeLoadingHeaderReserve || $[16] !== t7) {
    t8 = {
      [STREAM_ROUTE_LOADING_BOTTOM_RESERVE]: t7,
      [STREAM_ROUTE_LOADING_HEADER_RESERVE]: routeLoadingHeaderReserve
    };
    $[15] = routeLoadingHeaderReserve;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const streamRouteLoadingReserveStyle = t8;
  const safeAreaClass = !isNavVisible && !isKeyboardVisible ? "tw-pb-[env(safe-area-inset-bottom,0px)]" : "";
  let activeContent;
  if (activeView === "messages") {
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <BrainMobileMessages />;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    activeContent = t9;
  } else {
    if (activeView === "waves") {
      let t9;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <WavesQuickVoteView />;
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      activeContent = t9;
    } else {
      let t9;
      if ($[20] !== children) {
        t9 = <main>{children}</main>;
        $[20] = children;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      activeContent = t9;
    }
  }
  const t9 = `${safeAreaClass} ${"tw-overflow-auto"}`;
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <PullToRefresh triggerZoneRef={headerRef} />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <TouchDeviceHeader />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== headerWrapperRef) {
    t12 = <div ref={headerWrapperRef}>{t11}</div>;
    $[24] = headerWrapperRef;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== isSingleDropOpen || $[27] !== isStreamRoute) {
    t13 = !isSingleDropOpen && !isStreamRoute && <div className="tw-h-[85px] tw-w-full" />;
    $[26] = isSingleDropOpen;
    $[27] = isStreamRoute;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== isEditingOnMobile || $[30] !== isSingleDropOpen || $[31] !== shouldHideBottomNav) {
    t14 = !isSingleDropOpen && !isEditingOnMobile && <BottomNavigation hidden={shouldHideBottomNav} />;
    $[29] = isEditingOnMobile;
    $[30] = isSingleDropOpen;
    $[31] = shouldHideBottomNav;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== activeContent || $[34] !== streamRouteLoadingReserveStyle || $[35] !== t12 || $[36] !== t13 || $[37] !== t14 || $[38] !== t9) {
    t15 = <div className={t9} style={streamRouteLoadingReserveStyle}>{t10}{t12}{activeContent}{t13}{t14}</div>;
    $[33] = activeContent;
    $[34] = streamRouteLoadingReserveStyle;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t9;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}
export default function AppLayout(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Suspense fallback={<AppLayoutFallback>{children}</AppLayoutFallback>}><AppLayoutContent>{children}</AppLayoutContent></Suspense>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}